import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { DayPicker } from 'react-day-picker';
import { es } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { cva, VariantProps } from 'class-variance-authority';
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from './Select';
import { buttonVariants } from './Button';

export type CalendarProps = React.ComponentProps<typeof DayPicker> &
	VariantProps<typeof calendarVariants> & {
		onDateRangeSelect?: (range: { from: Date; to: Date } | undefined) => void;
		selectedYear?: number;
		onYearChange?: (year: number) => void;
	};

const calendarVariants = cva('p-3', {
	variants: {
		size: {
			small: 'text-sm',
			medium: 'text-base',
			large: 'text-lg',
		},
		styleCustom: {
			default: 'dark:bg-dark text-black dark:text-dark-text',
			dark: 'bg-gray-800 text-white',
			colorful: 'bg-gradient-to-r from-pink-500 via-red-500 to-yellow-500 text-white',
		},
		range: {
			default: {
				start: 'bg-blue-800 text-white',
				end: 'bg-blue-800 text-white',
				middle: 'bg-blue-400 text-white',
			},
			colorful: {
				start: 'bg-purple-800 text-white',
				end: 'bg-purple-800 text-white',
				middle: 'bg-purple-400 text-white',
			},
		},
	},
	defaultVariants: {
		size: 'medium',
		styleCustom: 'default',
		range: 'default',
	},
});

interface YearSelectorProps {
	selectedYear: number;
	onYearChange: (year: number) => void;
}

const YearSelector: React.FC<YearSelectorProps> = ({ selectedYear, onYearChange }) => {
	const currentYear = new Date().getFullYear();
	const years = Array.from({ length: 100 }, (_, i) => currentYear - 50 + i);

	return (
		<Select
			onValueChange={value => onYearChange(Number(value))}
			value={selectedYear.toString()}>
			<SelectTrigger className="w-[180px]">
				<SelectValue placeholder="Selecciona un año" />
			</SelectTrigger>
			<SelectContent style={{ zIndex: 1504 }}>
				<SelectGroup>
					{years.map(year => (
						<SelectItem key={year} value={year.toString()}>
							{year}
						</SelectItem>
					))}
				</SelectGroup>
			</SelectContent>
		</Select>
	);
};

function Calendar({
	className,
	classNames,
	showOutsideDays = true,
	size,
	styleCustom,
	range,
	selectedYear,
	onYearChange,
	...props
}: CalendarProps) {
	const today = new Date();

	// Mes inicial: prioriza el año externo, luego la fecha seleccionada, y por último hoy.
	const getInitialMonth = (): Date => {
		if (selectedYear) return new Date(selectedYear, today.getMonth(), 1);
		if (props.selected instanceof Date) return props.selected;
		return today;
	};

	const [currentMonth, setCurrentMonth] = React.useState<Date>(getInitialMonth);

	const handleMonthChange = (month: Date): void => {
		setCurrentMonth(month);
	};

	// Selección del año desde el desplegable superior: navega la cuadrícula a ese año
	// (manteniendo el mes visible) y notifica al padre.
	const handleYearSelect = (year: number): void => {
		setCurrentMonth(new Date(year, currentMonth.getMonth(), 1));
		onYearChange?.(year);
	};

	return (
		<div>
			<div className="flex items-center justify-center mb-2">
				<span className="mr-2">Año:</span>
				<YearSelector
					selectedYear={selectedYear || currentMonth.getFullYear()}
					onYearChange={handleYearSelect}
				/>
			</div>
			<DayPicker
				showOutsideDays={showOutsideDays}
				className={cn(calendarVariants({ size, styleCustom, range }), className)}
				classNames={{
					months: 'flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0 dark:bg-dark',
					month: 'space-y-4',
					caption: 'flex justify-center pt-1 relative items-center ',
					caption_label: 'text-sm font-medium ',
					nav: 'space-x-1 flex items-center ',
					nav_button: cn(
						buttonVariants({ variant: 'outline' }),
						'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100',
					),
					nav_button_previous: 'absolute left-1',
					nav_button_next: 'absolute right-1',
					table: 'w-full border-collapse space-y-1 ',
					head_row: 'flex',
					head_cell: 'text-muted-foreground rounded-md w-8 font-normal text-[0.8rem]',
					row: 'flex w-full mt-2',
					cell: cn(
						'relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-blue-200 [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected].day-range-end)]:rounded-r-md',
						props.mode === 'range'
							? '[&:has(>.day-range-end)]:rounded-r-md [&:has(>.day-range-start)]:rounded-l-md first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md'
							: '[&:has([aria-selected])]:rounded-md',
					),
					day: cn(
						buttonVariants({ variant: 'ghost' }),
						'h-8 w-8 p-0 font-normal aria-selected:opacity-100',
					),
					day_range_start: 'day-range-start',
					day_range_end: 'day-range-end',
					day_selected:
						'bg-blue-500 text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-blue-600 focus:text-primary-foreground',
					day_today: 'bg-blue-400',
					day_outside:
						'day-outside text-muted-foreground aria-selected:bg-accent/50 aria-selected:text-muted-foreground',
					day_disabled: 'text-muted-foreground opacity-50',
					day_range_middle:
						'aria-selected:bg-blue-200 aria-selected:text-accent-foreground',
					day_hidden: 'invisible',
					...classNames,
				}}
				components={{
					IconLeft: () => <ChevronLeft className="h-4 w-4" />,
					IconRight: () => <ChevronRight className="h-4 w-4" />,
				}}
				locale={es}
				weekStartsOn={1}
				{...props}
				month={currentMonth}
				onMonthChange={handleMonthChange}
			/>
		</div>
	);
}
Calendar.displayName = 'Calendar';

export { Calendar };
